import React from 'react';
import { Button, ButtonProps, Tooltip } from 'antd';
import { PermissionName } from './permissionsConstants';
import { usePermission } from '../../hooks/usePermission';
import { checkPermission } from '../../utils/permissionUtils';

interface PermissionButtonProps extends ButtonProps {
  /**
   * needed permission
   */
  permission?: PermissionName | PermissionName[];

  /** 
   * needed any permission
   */
  anyPermission?: PermissionName[];

  /**
   * needed all permissions
   */
  allPermissions?: PermissionName[];

  /**
   * hide button when no permission (default false, will disable button)
   */
  hideOnNoPermission?: boolean;

  /**
   * no permission tooltip text
   */
  noPermissionTooltip?: string;

  /**
   * children (optional, support only icon button)
   */
  children?: React.ReactNode;
}

export const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  anyPermission,
  allPermissions,
  hideOnNoPermission = false,
  noPermissionTooltip = 'You do not have permission to perform this action',
  children,
  disabled,
  ...buttonProps
}) => {
  const {
    hasPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions
  } = usePermission();

  // Use unified permission check function
  const hasAccess = checkPermission({
    permission,
    anyPermission,
    allPermissions,
    hasPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions
  });

  // hide button when no permission
  if (!hasAccess && hideOnNoPermission) {
    return null;
  }

  // disable button and show tooltip when no permission
  if (!hasAccess) {
    return (
      <Tooltip title={noPermissionTooltip}>
        <Button {...buttonProps} disabled={true}>
          {children}
        </Button>
      </Tooltip>
    );
  }

  // normal render when has permission
  return (
    <Button {...buttonProps} disabled={disabled}>
      {children}
    </Button>
  );
};

export default PermissionButton;

